import React from 'react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { performanceMonitor } from '@/utils/performanceMonitor';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import { scrollToSectionWithAnimation } from '@/utils/sectionTransitions';

import { HomeSection } from '@/hooks/useHomeSections';
import { useHeaderSize } from '@/hooks/useHeaderSize';
import { AnimatedAbaskLogo } from './AnimatedAbaskLogo';

interface AbaskHeroSectionProps {
  section: HomeSection;
  className?: string;
}

export const AbaskHeroSection: React.FC<AbaskHeroSectionProps> = ({ 
  section, 
  className 
}) => {
  const { language } = useLanguage();
  useHeaderSize();
  
  // Performance monitoring
  const endTimer = React.useMemo(() => 
    performanceMonitor.startTimer('abask-hero'), 
    [section.updated_at]
  );
  
  React.useEffect(() => {
    return () => endTimer();
  }, [endTimer]);

  // Enhanced smooth scroll to next section with impressive transitions
  const handleCtaClick = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    
    // Use the advanced animation system
    const success = scrollToSectionWithAnimation('#section-1', '#section-0', 'heroToNext');
    
    if (!success) {
      // Fallback to simple scroll if animation fails
      const nextSection = document.querySelector('#section-1') as HTMLElement;
      if (nextSection) {
        nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []);

  // Get localized content
  const title = language === 'ar' ? section.title_ar : section.title_en;
  const subtitle = language === 'ar' ? section.subtitle_ar : section.subtitle_en;
  const content = language === 'ar' ? section.content_ar : section.content_en;
  const buttonText = language === 'ar' ? section.button_text_ar : section.button_text_en;

  // Sanitize content
  const safeContent = React.useMemo(() => {
    if (!content) return '';
    return sanitizeHtml(content);
  }, [content]);

  const safeButtonText = React.useMemo(() => {
    if (!buttonText) return '';
    return sanitizeHtml(buttonText);
  }, [buttonText]);

  return (
    <>
      {/* Skip to content link */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-primary text-primary-foreground px-3 py-2 rounded z-50"
      >
        {language === 'ar' ? 'تخطي إلى المحتوى' : 'Skip to content'}
      </a>
      
      <section
        id="section-hero"
        role="banner"
        aria-labelledby="hero-title"
        className={cn(
          'abask-hero relative w-full flex items-center justify-center overflow-hidden',
          className
        )}
        style={{
          '--brand-primary': '#1f3363',
          '--brand-gold': '#C6A15B',
          '--overlay': 'rgba(0,0,0,.45)'
        } as React.CSSProperties}
      >
        {/* Content Container with header offset */}
        <div className="relative z-10 container mx-auto px-4 pt-0 pb-8 sm:pb-10 md:pb-12">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center gap-3 sm:gap-4 md:gap-6 lg:gap-8 content-stack">
            
            {/* Animated Logo */}
            <div data-hero-logo className="mb-6 sm:mb-8 md:mb-10 lg:mb-12 flex items-center justify-center" style={{ width: 'min(66vw, 520px)', maxWidth: 'min(80vw, 560px)', maxHeight: 'min(22vh, 180px)', paddingBottom: 'clamp(12px, 3vh, 24px)', marginBottom: 'clamp(32px, 10vh, 96px)', marginLeft: 'auto', marginRight: 'auto' }}>
              <AnimatedAbaskLogo 
                size="desktop" 
                isAnimated={true}
                className="mx-auto drop-shadow-2xl w-full h-auto"
              />
            </div>

            {/* Title - Improved responsive text sizing */}
            {title && (
              <h1 
                id="hero-title"
                className={cn(
                  'text-white font-bold leading-tight mb-3 md:mb-4',
                  'text-[clamp(18px,5.5vw,28px)] sm:text-[clamp(22px,5vw,34px)] md:text-[clamp(26px,4vw,42px)] lg:text-[clamp(30px,3.4vw,50px)] xl:text-[clamp(34px,3vw,58px)]',
                  language === 'en' && 'tracking-tight'
                )}
                style={{ 
                  maxWidth: '95%', 
                  margin: '0 auto 0.75rem auto',
                  lineHeight: '1.1'
                }}
              >
                {title}
              </h1>
            )}

            {/* Subtitle - Much more responsive */}
            {subtitle && (
              <p className={cn(
                'text-white/90 mb-2 sm:mb-3 md:mb-4 leading-relaxed',
                'text-[clamp(14px,4vw,18px)] sm:text-[clamp(16px,3.5vw,20px)] md:text-[clamp(18px,3vw,22px)] lg:text-[clamp(20px,2.4vw,26px)] xl:text-[clamp(22px,2vw,30px)]',
                'max-w-3xl mx-auto font-medium'
              )}>
                {subtitle}
              </p>
            )}

            {/* Content/Description - Responsive text */}
            {safeContent && (
              <div className={cn(
                'text-white/85 mb-3 sm:mb-4 md:mb-5 leading-relaxed',
                'text-[clamp(12px,2.5vw,14px)] sm:text-[clamp(13px,2.3vw,15px)] md:text-[clamp(14px,2vw,16px)] lg:text-base max-w-2xl mx-auto'
              )}>
                <div 
                  className="whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: safeContent }}
                />
              </div>
            )}

            {/* CTA Button - Responsive sizing */}
            {safeButtonText && (
              <div className="flex justify-center pb-3 sm:pb-4 md:pb-6">
                <button
                  onClick={handleCtaClick}
                  aria-label={`${safeButtonText} - ${language === 'ar' ? 'انتقل للقسم التالي' : 'Go to next section'}`}
                  className={cn(
                    'inline-flex items-center',
                    'px-4 py-2 sm:px-5 sm:py-3 md:px-6 md:py-3 lg:px-8 lg:py-4',
                    'bg-[var(--brand-gold)] text-[var(--brand-primary)] font-semibold',
                    'rounded-lg transition-all duration-200',
                    'hover:bg-[#B8975A] hover:transform hover:scale-105',
                    'focus:outline-none focus:ring-2 focus:ring-[var(--brand-gold)] focus:ring-offset-2 focus:ring-offset-black/20',
                    'min-h-[40px] sm:min-h-[44px] min-w-[100px] sm:min-w-[120px]', // Touch target
                    'text-xs sm:text-sm md:text-base'
                  )}
                >
                  <span dangerouslySetInnerHTML={{ __html: safeButtonText }} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Performance optimized styles + robust min-height with header offset */}
        <style dangerouslySetInnerHTML={{
          __html: `
            .abask-hero { 
              --header-offset: calc(var(--header-h, 64px) + clamp(8px, 2vh, 24px));
              min-height: 100vh; 
              min-height: 100svh;
              display: flex;
              flex-direction: column;
            }
            
            /* Header-aware content positioning */
            .abask-hero .container {
              flex: 1;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              min-height: calc(100svh - var(--header-h, 64px));
              padding-top: var(--header-offset);
            }
            
            /* Responsive logo sizing safeguarded by viewport height */
            .abask-hero [data-hero-logo] { 
              width: min(62vw, 22vh, 480px);
              margin-bottom: clamp(18px, 6vh, 56px);
            }
            .abask-hero .content-stack { gap: clamp(8px, 1.8vh, 24px); }
            .abask-hero [data-hero-logo] svg { --stroke-width: 2.2; }
            
            @media (max-width: 480px) {
              .abask-hero [data-hero-logo] { width: min(64vw, 18vh, 380px); margin-bottom: clamp(20px, 7vh, 60px); }
            }
            
            @media (max-height: 740px) {
              .abask-hero [data-hero-logo] { 
                width: min(54vw, 18vh, 420px);
                margin-bottom: clamp(18px, 7vh, 60px);
              }
              .abask-hero [data-hero-logo] svg { --stroke-width: 2; }
              .abask-hero h1 { font-size: clamp(16px, 4.6vw, 42px); }
              .abask-hero p { font-size: clamp(12px, 3.2vw, 20px); }
            }
            @media (max-height: 640px) {
              .abask-hero [data-hero-logo] { 
                width: min(52vw, 18vh, 400px);
                margin-bottom: clamp(28px, 14vh, 96px);
              }
              .abask-hero [data-hero-logo] svg { --stroke-width: 1.8; }
              .abask-hero [data-hero-logo] svg #abask-logo-outline { filter: none !important; }
              .abask-hero h1 { font-size: clamp(15px, 4.4vw, 40px); }
              .abask-hero p { font-size: clamp(11px, 3.2vw, 18px); }
            }
            @media (max-height: 580px) {
              .abask-hero [data-hero-logo] { 
                width: min(48vw, 16vh, 360px);
                margin-bottom: clamp(24px, 16vh, 96px);
              }
              .abask-hero [data-hero-logo] svg { --stroke-width: 1.6; }
              .abask-hero h1 { font-size: clamp(14px, 4vw, 36px); }
              .abask-hero p { font-size: clamp(10px, 2.8vw, 16px); }
            }
            
            /* Ensure no horizontal gaps on mobile */
            @media (max-width: 768px) {
            
            /* Ensure no horizontal gaps on mobile */
            @media (max-width: 768px) {
              .abask-hero { 
                width: 100vw; 
              }
              
              .abask-hero .container {
                min-height: calc(100svh - var(--header-h, 64px));
                padding-top: var(--header-offset);
              }
            }
            
            /* Tablet and up - keep perfect center */
            @media (min-width: 769px) {
              .abask-hero .container {
                min-height: calc(100svh - var(--header-h, 64px));
                padding-top: var(--header-offset);
              }
            }
            
            @media (prefers-reduced-motion: reduce) {
              .abask-hero * {
                animation-duration: 0.01ms !important;
                animation-iteration-count: 1 !important;
                transition-duration: 0.01ms !important;
              }
              
              .abask-hero button:hover {
                transform: none !important;
              }
            }
          `
        }} />
      </section>
    </>
  );
};

export default AbaskHeroSection;